import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BookOpen, MapPin, CheckSquare, FileText, Bike, Shield, Map as MapIcon, Calendar, Download } from 'lucide-react';
import './KnowledgePage.css';

export default function KnowledgePage() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: t('knowledge.all'), icon: '📚' },
    { id: 'guides', name: t('knowledge.guides'), icon: '📖' },
    { id: 'regions', name: t('knowledge.regions'), icon: '🗺️' },
    { id: 'checklists', name: t('knowledge.checklists'), icon: '✅' },
    { id: 'stories', name: t('knowledge.stories'), icon: '📰' }
  ];

  const guides = [
    {
      id: 'equipment',
      category: 'guides',
      title: t('knowledge.guide_equipment'),
      description: t('knowledge.guide_equipment_desc'),
      icon: '🎒',
      link: '/wissen/ausruestung'
    },
    {
      id: 'technique',
      category: 'guides',
      title: t('knowledge.guide_technique'),
      description: t('knowledge.guide_technique_desc'),
      icon: '🚴',
      link: '/wissen/fahrtechnik'
    },
    {
      id: 'safety',
      category: 'guides',
      title: t('knowledge.guide_safety'),
      description: t('knowledge.guide_safety_desc'),
      icon: '🦺',
      link: '/wissen/sicherheit'
    },
    {
      id: 'traffic',
      category: 'guides',
      title: t('knowledge.guide_traffic'),
      description: t('knowledge.guide_traffic_desc'),
      icon: '🚦',
      link: '/wissen/verkehrsregeln'
    },
    {
      id: 'ebike',
      category: 'guides',
      title: t('knowledge.guide_ebike'),
      description: t('knowledge.guide_ebike_desc'),
      icon: '⚡',
      link: '/wissen/ebike'
    }
  ];

  const regions = [
    {
      id: 'highlands',
      category: 'regions',
      title: t('knowledge.region_highlands'),
      description: t('knowledge.region_highlands_desc'),
      season: t('knowledge.season_may_sep'),
      image: '🏔️',
      link: '/wissen/region/highlands'
    },
    {
      id: 'islands',
      category: 'regions',
      title: t('knowledge.region_islands'),
      description: t('knowledge.region_islands_desc'),
      season: t('knowledge.season_jun_aug'),
      image: '🏝️',
      link: '/wissen/region/islands'
    },
    {
      id: 'lowlands',
      category: 'regions',
      title: t('knowledge.region_lowlands'),
      description: t('knowledge.region_lowlands_desc'),
      season: t('knowledge.season_apr_oct'),
      image: '🌳',
      link: '/wissen/region/lowlands'
    },
    {
      id: 'edinburgh',
      category: 'regions',
      title: t('knowledge.region_edinburgh'),
      description: t('knowledge.region_edinburgh_desc'),
      season: t('knowledge.season_year_round'),
      image: '🏰',
      link: '/wissen/region/edinburgh'
    }
  ];

  const checklists = [
    {
      id: 'packing',
      category: 'checklists',
      title: t('knowledge.checklist_packing'),
      description: t('knowledge.checklist_packing_desc'),
      icon: '🎒',
      link: '/wissen/checklist/packlist'
    },
    {
      id: 'bike-check',
      category: 'checklists',
      title: t('knowledge.checklist_bike'),
      description: t('knowledge.checklist_bike_desc'),
      icon: '🔧',
      link: '/wissen/checklist/bike'
    },
    {
      id: 'emergency',
      category: 'checklists',
      title: t('knowledge.checklist_emergency'),
      description: t('knowledge.checklist_emergency_desc'),
      icon: '🚑',
      link: '/wissen/checklist/notfall'
    }
  ];

  const stories = [
    {
      id: 'nc500',
      category: 'stories',
      title: t('knowledge.story_nc500'),
      description: t('knowledge.story_nc500_desc'),
      author: 'Sarah M.',
      date: 'September 2024',
      image: '📸',
      link: '/wissen/story/nc500'
    },
    {
      id: 'skye',
      category: 'stories',
      title: t('knowledge.story_skye'),
      description: t('knowledge.story_skye_desc'),
      author: 'Thomas K.',
      date: 'August 2024',
      image: '🏝️',
      link: '/wissen/story/skye'
    },
    {
      id: 'whisky',
      category: 'stories',
      title: t('knowledge.story_whisky'),
      description: t('knowledge.story_whisky_desc'),
      author: 'Anna B.',
      date: 'Juli 2024',
      image: '🥃',
      link: '/wissen/story/whisky'
    }
  ];

  const allContent = [...guides, ...regions, ...checklists, ...stories];
  const filteredContent = selectedCategory === 'all' 
    ? allContent 
    : allContent.filter(item => item.category === selectedCategory);

  return (
    <div className="knowledge-page" data-testid="knowledge-page">
      <header className="knowledge-header">
        <div className="header-content">
          <BookOpen size={48} className="header-icon" />
          <div>
            <h1>{t('knowledge.title')}</h1>
            <p>{t('knowledge.subtitle')}</p>
          </div>
        </div>
      </header>

      <div className="knowledge-container">
        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span className="cat-icon">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        <div className="content-grid">
          {filteredContent.map(item => (
            <div key={item.id} className="content-card">
              <div className="card-icon">{item.icon || item.image}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              
              {item.season && (
                <div className="season-badge">
                  <Calendar size={16} />
                  {item.season}
                </div>
              )}
              
              {item.author && (
                <div className="story-meta">
                  <span>{item.author}</span>
                  <span>{item.date}</span>
                </div>
              )}

              {item.pdf ? (
                <a href={item.pdf} className="card-link" download>
                  <Download size={18} />
                  {t('knowledge.download_pdf')}
                </a>
              ) : (
                <Link to={item.link} className="card-link">
                  {t('knowledge.learn_more')} →
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="knowledge-stats">
          <div className="stat-card">
            <BookOpen size={32} />
            <div>
              <h3>5</h3>
              <p>{t('knowledge.stats_guides')}</p>
            </div>
          </div>
          <div className="stat-card">
            <MapIcon size={32} />
            <div>
              <h3>4</h3>
              <p>{t('knowledge.stats_regions')}</p>
            </div>
          </div>
          <div className="stat-card">
            <CheckSquare size={32} />
            <div>
              <h3>3</h3>
              <p>{t('knowledge.stats_checklists')}</p>
            </div>
          </div>
          <div className="stat-card">
            <FileText size={32} />
            <div>
              <h3>3</h3>
              <p>{t('knowledge.stats_stories')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
